/**
 * Checkbox Control
 * Boolean checkbox field
 * Performance: Memoized, useCallback for event handlers
 */

import React, { useCallback, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

const CheckboxInput = memo(React.forwardRef<HTMLInputElement, ControlRendererProps>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      touched,
      disabled,
      mode,
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked);
      },
      [onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    return (
      <div className={containerClassName}>
        <div className="control__checkbox-wrapper">
          <input
            ref={ref}
            id={control.id}
            type="checkbox"
            name={control.id}
            checked={Boolean(value)}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={disabled || control.disabled}
            className="control__input control__input--checkbox"
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={`${control.id}-error ${control.id}-help`}
          />

          {control.label && (
            <label htmlFor={control.id} className="control__label control__label--checkbox">
              <span className="control__label-text">{control.label}</span>
              {control.required && (
                <span className="control__required" aria-label="required">
                  *
                </span>
              )}
            </label>
          )}

          {showError && (
            <span className="control__error-icon" aria-hidden="true">
              <WarningIcon width={14} height={14} />
            </span>
          )}
        </div>

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

CheckboxInput.displayName = 'CheckboxInput';

export default CheckboxInput;
